import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import {
  generateOtpCode,
  verifyOtp,
  verifyClearError,
  smsOtpVerified
} from "../../store/actions/otp";
import { getOtp } from "../../store/selectors/otp";
import {
  getApplicantInfo,
  getIsEmailChanged,
  getIsMultiPartnerJourney,
  getIsSignatoryPrimaryEmailChanged,
  getProspectId
} from "../../store/selectors/appConfig";
import { getsIsContactAndSignatoryDetailsChanged } from "../../store/selectors/sendProspectToAPI";
import { useTrackingHistory } from "../../utils/useTrackingHistory";

import { MAX_ATTEMPT_ALLOWED } from "./constants";
import { Form } from "./components/Form";
import { OtpChannel } from "../../constants";
import { triggerDecisions } from "../../store/actions/decisions";
import { getDocumentsList } from "../../store/actions/uploadDocuments";
import routes from "../../routes";
import { updateProspectId } from "../../store/actions/appConfig";
import { resetLogin } from "../../store/actions/loginForm";

export const Otp = ({
  redirectRoute,
  otpType,
  title,
  info,
  changeText,
  roInviteProp,
  heading,
  selectedPartner,
  partnerInviteProp,
  isFromPreview
}) => {
  const dispatch = useDispatch();
  const {
    attempts,
    verificationError,
    isVerified,
    isPending,
    isGenerating,
    maxAttemptsExceeded,
    maxAttemptsServerError
  } = useSelector(getOtp);
  const prospectId = useSelector(getProspectId);
  const isMultiPartnerJourney = useSelector(getIsMultiPartnerJourney);
  const applicantInfo = useSelector(getApplicantInfo);
  const isContactAndSignatoryDetailsChanged = useSelector(getsIsContactAndSignatoryDetailsChanged);
  const isEmailChanged = useSelector(getIsEmailChanged);
  const isSignatoryEmailIdChanged = useSelector(getIsSignatoryPrimaryEmailChanged);

  const searchParam = useLocation().search;
  const pushHistory = useTrackingHistory();

  const [code, setCode] = useState(Array(6).fill(""));
  const [loginAttempt, setLoginAttempt] = useState(0);

  const otpRef = useRef(null);

  const resetOtpForm = useCallback(() => {
    setCode(Array(6).fill(""));
    otpRef.current.resetFocus();
  }, [setCode]);

  //if user is verified
  useEffect(() => {
    if (isVerified) {
      if (
        otpType === OtpChannel.Sms &&
        redirectRoute !== routes.MyApplications &&
        redirectRoute !== routes.efrOTPVerification &&
        redirectRoute !== routes.stakeholdersInfo &&
        !partnerInviteProp
      ) {
        if (isMultiPartnerJourney && selectedPartner && Object.keys(selectedPartner).length > 0) {
          if (isEmailChanged || isSignatoryEmailIdChanged) {
            dispatch(smsOtpVerified());
            const {
              signatoryId,
              contactDetails: { primaryEmail, primaryMobCountryCode, primaryMobileNo }
            } = selectedPartner;

            const otpData = {
              email: primaryEmail,
              mobileNo: primaryMobileNo,
              countryCode: primaryMobCountryCode,
              sid: signatoryId,
              prospectId,
              mode: OtpChannel.Email,
              action: "generate"
            };

            dispatch(generateOtpCode(otpData));
          }
        } else if (!isContactAndSignatoryDetailsChanged) {
          dispatch(smsOtpVerified());
          const otpData = {
            ...applicantInfo,
            prospectId,
            mode: OtpChannel.Email,
            action: "generate"
          };

          dispatch(generateOtpCode(otpData));
        }
      }
      if (otpType === OtpChannel.Email) {
        if (prospectId) {
          dispatch(
            triggerDecisions({
              onValuesChanged: changedValues => {
                // console.log(changedValues);
              },
              inputFields: {
                decision_input: [
                  {
                    input_key: "prospect.applicantInfo.persona",
                    input_value: applicantInfo.persona
                  }
                ]
              }
            })
          );

          //document upload init
          dispatch(getDocumentsList());
        }
      }

      if (!roInviteProp && !partnerInviteProp) {
        pushHistory(`${redirectRoute}${searchParam}`, true, { isFromPreview });
      }
    }
  }, [isVerified, redirectRoute, selectedPartner]);

  useEffect(() => {
    dispatch(resetLogin());
  }, []);

  useEffect(() => {
    if (isVerified) {
      if (roInviteProp) {
        dispatch(updateProspectId(roInviteProp.prospectId));
        roInviteProp.getProspectInfo();
      }
      if (partnerInviteProp) {
        dispatch(updateProspectId(partnerInviteProp.partnerProspectId));
        partnerInviteProp.getProspectInfo();
      }
    }
  }, [isVerified]);

  useEffect(() => {
    if (verificationError) {
      resetOtpForm();
    }
  }, [verificationError, resetOtpForm]);

  useEffect(() => () => dispatch(verifyClearError()), [dispatch]);

  const handleSendNewCodeLinkClick = useCallback(() => {
    resetOtpForm();
    if (!isGenerating) {
      /* istanbul ignore else */
      if (loginAttempt < MAX_ATTEMPT_ALLOWED) {
        const otpData = {
          ...applicantInfo,
          ...(partnerInviteProp && {
            mobileNo: partnerInviteProp.mobileNo,
            email: partnerInviteProp.email,
            countryCode: partnerInviteProp.countryCode
          }),
          ...(partnerInviteProp?.sid && { sid: partnerInviteProp.sid }),
          ...(partnerInviteProp?.partnerProspectId && {
            prospectId: partnerInviteProp.partnerProspectId
          })
        };
        otpData.action = "resend";
        otpData.mode = otpType;
        dispatch(generateOtpCode(otpData));
      }
      setLoginAttempt(loginAttempt + 1);
    }
  }, [isGenerating, loginAttempt, dispatch, applicantInfo, resetOtpForm]);

  const submitForm = useCallback(() => {
    let verifyOtpData = {
      code: code.join(""),
      mode: otpType,
      ...(partnerInviteProp && {
        prospectId: partnerInviteProp.partnerProspectId,
        mobileNo: partnerInviteProp.mobileNo,
        email: partnerInviteProp.email,
        sid: partnerInviteProp.sid,
        countryCode: partnerInviteProp.countryCode
      })
    };

    dispatch(verifyOtp({ ...verifyOtpData }));
  }, [dispatch, code, partnerInviteProp]);

  return (
    <Form
      info={info}
      title={title}
      otpType={otpType}
      changeText={changeText}
      applicantInfo={applicantInfo}
      attempts={attempts}
      loginAttempt={loginAttempt}
      isPending={isPending}
      isGenerating={isGenerating}
      verificationError={verificationError}
      otpRef={otpRef}
      code={code}
      setCode={setCode}
      handleSendNewCodeLinkClick={handleSendNewCodeLinkClick}
      submitForm={submitForm}
      maxServerAttemptsExceeded={maxAttemptsExceeded}
      maxAttemptsServerError={maxAttemptsServerError}
      heading={heading}
    />
  );
};

/**
 * PropTypes for Otp component
 */
Otp.propTypes = {
  /** Route to redirect to after successful OTP verification */
  redirectRoute: PropTypes.string,
  /** Type of OTP channel (SMS or Email) */
  otpType: PropTypes.string,
  /** Title to display on the OTP form */
  title: PropTypes.string,
  /** Information text to display on the OTP form (can be string or React element) */
  info: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Text for change/edit link */
  changeText: PropTypes.string,
  /** Heading text for the OTP form (can be string or React element) */
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Flag indicating if navigation is from preview */
  isFromPreview: PropTypes.bool,
  /** Relationship Officer invitation properties */
  roInviteProp: PropTypes.shape({
    /** Prospect ID for RO invitation */
    prospectId: PropTypes.string,
    /** Function to get prospect information */
    getProspectInfo: PropTypes.func
  }),
  /** Selected partner information for multi-partner journey */
  selectedPartner: PropTypes.shape({
    /** Signatory ID */
    signatoryId: PropTypes.string,
    /** Contact details for the selected partner */
    contactDetails: PropTypes.shape({
      /** Primary email address */
      primaryEmail: PropTypes.string,
      /** Primary mobile country code */
      primaryMobCountryCode: PropTypes.string,
      /** Primary mobile number */
      primaryMobileNo: PropTypes.string
    })
  }),
  /** Partner invitation properties */
  partnerInviteProp: PropTypes.shape({
    /** Partner prospect ID */
    partnerProspectId: PropTypes.string,
    /** Mobile number for partner invitation */
    mobileNo: PropTypes.string,
    /** Email address for partner invitation */
    email: PropTypes.string,
    /** Signatory ID for partner invitation */
    sid: PropTypes.string,
    /** Country code for partner mobile number */
    countryCode: PropTypes.string,
    /** Function to get prospect information */
    getProspectInfo: PropTypes.func
  })
};
